import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { lastValueFrom } from 'rxjs';
import { HeaderComponent, NavComponent, NavigationItem } from '@cvx/nextpage';
import { CalAngularService } from '@cvx/cal-angular';
import { UserDetails } from './core/interface/app-user.interface';
import { APP_ROUTES, NAVIGATION_ITEMS } from './core/constants/app-navigation.constants';
import { Spinner } from './pages/spinner/spinner';
import { AppCommonService } from './core/services/app-common.service';
import { AuthTokenService } from './core/services/app-auth-token.service';
import { Toast } from './shared/components/toast/toast';
import { AuditTrailModal } from './shared/components/audit-trail-modal/audit-trail-modal';
import { TaxAccountingEdit } from './components/tax-accounting-page/tax-accounting-edit/tax-accounting-edit';
import { AppEditExistingIssue } from './shared/components/app-edit-existing-issue/app-edit-existing-issue';
import { AppModalService } from './core/services/app-modal.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    Spinner,
    HeaderComponent,
    NavComponent,
    RouterOutlet,
    AuditTrailModal,
    TaxAccountingEdit,
    Toast,
    AppEditExistingIssue,
  ],
  templateUrl: './app.html',
  styleUrl: './app.scss',
})
export class App implements OnInit {
  navigationItems: NavigationItem[] = [];
  welcomeMessage = '';
  isIssueEditModalVisible: boolean = false;
  isLoading = signal(true);
  isUnauthorizedUser = signal(false);
  readonly currentYear = new Date().getFullYear();

  private readonly calService = inject(CalAngularService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly commonService = inject(AppCommonService);
  private readonly modalService = inject(AppModalService);
  private readonly tokenService = inject(AuthTokenService);

  ngOnInit(): void {
    this.modalService.isEditIssueVisible$.subscribe((status: boolean) => {
      this.isIssueEditModalVisible = status;
    });
    this.logIN();
  }

  // Handles the user login process
  async logIN() {
    await this.getUserImpersonationToken();
    this.calService
      .isUserSignedIn()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isSignedIn) => {
        if (isSignedIn) {
          void this.initializeUserSession();
        }
      });
  }

  // Initializes the user session after successful sign-in
  private async initializeUserSession(): Promise<void> {
    const data = await lastValueFrom(this.calService.userInitiatedSignIn());
    const name = data?.name ?? '';
    if (!name) {
      return;
    }
    const cleanedName = name.replace(/\s*\[.*\]/, '').replace(/\s*,\s*/, ' ');
    const [lastName, firstName] = cleanedName.split(' ');

    const user: UserDetails = {
      firstName,
      lastName,
      emailId: data.email,
      userId: data.objectId,
      fullName: `${firstName} ${lastName}`,
      roles: data.roles,
      isUser: data.isUser,
      cai: data.logOnName,
    };
    this.welcomeMessage = `Hi! ${user.fullName}`;
    sessionStorage.setItem('currentUser', JSON.stringify(user));
    this.loadUserDetails();
  }

  // Fetches the user impersonation token from the authentication service
  async getUserImpersonationToken(): Promise<void> {
    try {
      await this.tokenService.getUserImpersonationToken();
    } catch (error) {
      console.error('Error while fetching user impersonation token:', error);
    }
  }

  // Loads the user details and sets up the navigation items based on the user's access level
  private loadUserDetails(): void {
    this.commonService
      .getUserDetails()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          const accessRole = response?.result?.access?.trim().toLowerCase() ?? '';
          const isAdmin = accessRole === 'admin';
          const isAdminAsc = accessRole === 'admin_asc';
          const hasAdministrativeAccess = accessRole === 'admin' || accessRole === 'admin_asc';
          const isEditor = accessRole === 'editor';
          sessionStorage.setItem(
            'hasAdministrativeAccess',
            JSON.stringify(hasAdministrativeAccess),
          );
          sessionStorage.setItem('isAdmin', JSON.stringify(isAdmin));
          sessionStorage.setItem('isAdminAsc', JSON.stringify(isAdminAsc));
          sessionStorage.setItem('isEditor', JSON.stringify(isEditor));
          this.navigationItems = this.buildNavigationItems(isAdmin);
          this.isLoading.set(false);
        },
        error: (error) => {
          sessionStorage.setItem('hasAdministrativeAccess', JSON.stringify(false));
          this.navigationItems = this.buildNavigationItems(false);
          this.attachLogoutButton();
          this.isLoading.set(false);
          const isUnauthorized =
            error?.status === 404 && /no user found with email/i.test(error?.error?.message ?? '');
          this.isUnauthorizedUser.set(isUnauthorized);
        },
        complete: () => {
          this.isLoading.set(false);
          this.attachLogoutButton();
        },
      });
  }

  // Handles the user sign-out process
  signOut(): void {
    this.calService.userInitiatedSignOut().subscribe();
  }

  // Builds the navigation items based on the user's administrative access
  private buildNavigationItems(hasAdministrativeAccess: boolean): NavigationItem[] {
    return hasAdministrativeAccess
      ? [...NAVIGATION_ITEMS]
      : NAVIGATION_ITEMS.filter((item) => item.link !== `/${APP_ROUTES.MAINTENANCE}`);
  }

  /** HEADER: Attach logout button to header welcome area */
  private attachLogoutButton(): void {
    window.setTimeout(() => {
      const login = document.getElementById('login');
      const logout = document.querySelector('.logout');
      if (login && logout) {
        login.appendChild(logout);
      }
    }, 100);
  }
}
